import { useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useParams } from "react-router";
import { ErrorBox, useTitle } from "../components/Layout.js";
import { Confirm } from "../components/Pairs.js";
import { T, TextsProvider } from "../components/texts.js";
import { Tone } from "../components/tone.js";
import { ApiError, api, type Intro } from "../data/api.js";
import { device, type SessionRecord, type StoredInvite } from "../data/device.js";
import { useIdentity } from "../data/identity.js";

// Stránka pozvánky `/{jazyk}/p/{token}` (REQ-034): token se uloží v zařízení a adresa ho hned
// ztratí (`/{jazyk}/p`), `noindex` a `Referrer-Policy: no-referrer`. „Udělat test“ nic v páru
// nezapíše, souhlas přijde až po testu. Neplatná pozvánka má jednu stránku pro všechny důvody.

type State = { kind: "loading" } | { kind: "gone" } | { kind: "error" } | { kind: "declined" } | { kind: "ready"; invite: StoredInvite; intro: Intro | null };

function useInvitePageMeta(): void {
  useEffect(() => {
    const metas = [
      Object.assign(document.createElement("meta"), { name: "robots", content: "noindex, nofollow" }),
      Object.assign(document.createElement("meta"), { name: "referrer", content: "no-referrer" }),
    ];
    for (const m of metas) document.head.append(m);
    return () => {
      for (const m of metas) m.remove();
    };
  }, []);
}

export function InvitePage() {
  const { lang = "cs", token: param } = useParams();
  const intl = useIntl();
  const navigate = useNavigate();
  const identity = useIdentity();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [mine, setMine] = useState<SessionRecord | null>(null);
  const [problem, setProblem] = useState<"self" | "mismatch" | null>(null);
  const [busy, setBusy] = useState(false);
  useInvitePageMeta();
  useTitle(intl.formatMessage({ id: "pairs.card.title" }));

  // Token z adresy do zařízení, adresa bez tokenu (NFR-002).
  useEffect(() => {
    if (param === undefined) return;
    void device.setPendingInvite({ token: param, inviter: "", slug: "" }).then(() => navigate(`/${lang}/p`, { replace: true }));
  }, [param, lang, navigate]);

  const load = useCallback(async () => {
    if (param !== undefined) return;
    setState({ kind: "loading" });
    const pending = await device.pendingInvite();
    if (pending === undefined) {
      setState({ kind: "gone" });
      return;
    }
    const accepted = await device.acceptedPair(pending.token);
    if (accepted !== undefined) {
      void navigate(`/${lang}/pairs/${accepted}`, { replace: true });
      return;
    }
    try {
      const info = await api.inviteLookup(pending.token);
      const invite = { token: pending.token, inviter: info.inviter_label, slug: info.methodology.slug };
      await device.setPendingInvite(invite);
      const intro = await api.intro(invite.slug, lang).catch(() => null);
      const own = (await device.sessions()).filter((s) => s.slug === invite.slug && s.origin === "user" && s.done === true);
      setMine(own.at(-1) ?? null);
      setState({ kind: "ready", invite, intro });
    } catch (e) {
      if (e instanceof ApiError && e.status === 410) {
        await device.clearPendingInvite();
        setState({ kind: "gone" });
      } else setState({ kind: "error" });
    }
  }, [param, lang, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  if (param !== undefined || state.kind === "loading") return null;
  if (state.kind === "error") return <ErrorBox onRetry={() => void load()} />;
  if (state.kind === "gone" || state.kind === "declined") {
    return (
      <div className="stack" data-testid={state.kind === "gone" ? "invite-gone" : "invite-declined"}>
        <h1>
          <FormattedMessage id={state.kind === "gone" ? "pairs.invite.gone.title" : "pairs.invite.declined"} />
        </h1>
        {state.kind === "gone" ? (
          <p className="muted">
            <FormattedMessage id="pairs.invite.gone.lead" />
          </p>
        ) : null}
        <Link className="button" to={`/${lang}/`}>
          <FormattedMessage id="claim.home" />
        </Link>
      </div>
    );
  }
  const { invite, intro } = state;

  async function useMine() {
    setBusy(true);
    setProblem(null);
    try {
      const session = mine === null ? undefined : { id: mine.id, token: mine.token };
      const r = await api.inviteAccept(invite.token, "invite_page", session);
      await device.setAcceptedPair(invite.token, r.pair_id);
      if (mine !== null) await device.setPairSession(r.pair_id, mine.id);
      await device.clearPendingInvite();
      void navigate(`/${lang}/pairs/${r.pair_id}`);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : "";
      if (code === "invite_gone") setState({ kind: "gone" });
      else setProblem(code === "pair_self" ? "self" : "mismatch");
    } finally {
      setBusy(false);
    }
  }

  const canUseMine = mine !== null || identity.kind === "in";
  return (
    <Tone slug={invite.slug}>
      <div className="stack invite" data-testid="invite">
        <h1>
          <FormattedMessage id="pairs.invite.title" values={{ name: invite.inviter }} />
        </h1>
        {intro === null ? null : (
          <TextsProvider texts={intro.texts} locale={lang} preview={false}>
            <p className="small muted">
              <FormattedMessage id="pairs.invite.test" values={{ test: <T k="methodology.name" /> }} /> · <FormattedMessage id="intro.items" values={{ items: intro.items }} />
            </p>
          </TextsProvider>
        )}
        <p>
          <FormattedMessage id="pairs.invite.lead" values={{ name: invite.inviter }} />
        </p>
        {problem === null ? null : (
          <p className="notice small" role="alert" data-testid="invite-problem">
            <FormattedMessage id={problem === "self" ? "pairs.invite.self" : "pairs.invite.mismatch"} />
          </p>
        )}
        <Link className="button" to={`/${lang}/test/${invite.slug}`} data-testid="invite-start">
          <FormattedMessage id="pairs.invite.start" />
        </Link>
        {canUseMine ? (
          <div className="card stack">
            <p className="small consent-text" data-testid="invite-consent">
              <FormattedMessage id="consent.pair_link.v1.accept" />
            </p>
            <button type="button" className="button button--quiet" disabled={busy} onClick={() => void useMine()} data-testid="invite-use-mine">
              <FormattedMessage id="pairs.invite.useMine" />
            </button>
          </div>
        ) : identity.kind === "anon" ? (
          <Link to={`/${lang}/me?next=${encodeURIComponent(`/${lang}/p`)}`} className="small">
            <FormattedMessage id="pairs.invite.loginForResult" />
          </Link>
        ) : null}
        <Confirm
          testId="invite-decline"
          label={<FormattedMessage id="pairs.invite.decline" />}
          text={<FormattedMessage id="consent.pair_link.v1.decline" />}
          yes={<FormattedMessage id="pairs.invite.decline" />}
          onConfirm={async () => {
            await api.inviteDecline(invite.token, "invite_page").catch((e: unknown) => {
              if (!(e instanceof ApiError && e.status === 410)) throw e;
            });
            await device.clearPendingInvite();
            setState({ kind: "declined" });
          }}
        />
        <p className="small muted" data-testid="invite-privacy">
          <FormattedMessage id="pairs.invite.privacy" /> <Link to={`/${lang}/privacy`}>
            <FormattedMessage id="pairs.invite.privacyLink" />
          </Link>
        </p>
      </div>
    </Tone>
  );
}
